import { Injectable } from '@angular/core';
import {
  Resolve,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
} from '@angular/router';
import { ProductService } from '@app/shared/services/product/product.service';
import {
  debounceTime,
  filter,
  finalize,
  map,
  switchMap,
  take,
  tap,
  catchError,
} from 'rxjs/operators';
import { OrderService } from './order.service';
import { Store } from '@ngrx/store';
import {
  CART_SELECTORS,
  dyfSelectors,
  MyOrdersStoreService,
} from '@usf/ngrx-order';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { MerchandisingService } from '@shared/services/merchandising/merchandising.service';
import {
  FREQUENTLY_BOUGHT_TOGETHER_EXPERIENCE,
  ReviewOrderMerchZone,
} from '@shared/constants/merchandising-constants';
import {
  LoadingState,
  selectMerchandisingResponseById,
} from '@usf/ngrx-product';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { CustomerSwitchStatus } from '@app/ngrx-customer/constants/customer-switch-status';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
@Injectable({
  providedIn: 'root',
})
export class OrderPageResolverResolver
  implements Resolve<Observable<number[]>>
{
  spinnerPromise: Promise<void>;
  private merchZoneSubscription: Subscription | null = null;
  private readyForProductSelection$: Observable<boolean>;
  constructor(
    private orderService: OrderService,
    private productService: ProductService,
    private store: Store,
    private merchandisingService: MerchandisingService,
    private customerStoreService: CustomerStoreService,
    private spinner: LoadingSpinnerService,
    private myOrdersStoreService: MyOrdersStoreService,
  ) {}
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<number[]> {
    this.spinnerPromise = this.spinner.createSpinnerModal(true);
    this.initializeReadyForProductSelection();
    this.fetchAndLoadFrequentlyBoughtTogether();
    this.loadMerchZoneProducts();
    return this.loadProducts();
  }
  private initializeReadyForProductSelection() {
    const customerState$ = this.orderService.getSelectedCustomerState$().pipe(
      filter(state => !!state),
      catchError(error => {
        return of(null);
      }),
    );

    const switchStatus$ = this.customerStoreService
      .customerSwitchStatus$()
      .pipe(filter(status => status === CustomerSwitchStatus.complete));

    const dyfProductState$ = this.store.select(dyfSelectors.selectDYF);

    const cartState$ = this.store
      .select(CART_SELECTORS.selectCart)
      .pipe(filter(cartState => !!cartState && !cartState.loading));

    const myOrdersState$ = this.myOrdersStoreService.state$().pipe(
      debounceTime(250),
      filter(state => {
        return (
          !!state &&
          !!state.ready &&
          !state.recentOrdersLoading &&
          !state.customerOrdersLoading &&
          !state.importedOrdersLoading &&
          !state.inProgressOrdersLoading
        );
      }),
    );

    this.readyForProductSelection$ = combineLatest([
      customerState$,
      cartState$,
      switchStatus$,
      dyfProductState$,
      myOrdersState$,
    ]).pipe(
      filter(
        ([
          customerState,
          cartState,
          switchStatus,
          dyfProductState,
          myOrdersState,
        ]) => {
          const isReady =
            myOrdersState &&
            switchStatus === CustomerSwitchStatus.complete &&
            !!cartState &&
            !cartState.loading &&
            !!dyfProductState &&
            !dyfProductState.loading &&
            !!customerState &&
            dyfProductState.customerNumber?.toString() ===
              customerState.customerNumber?.toString() &&
            dyfProductState.departmentNumber?.toString() ===
              customerState.departmentNumber?.toString() &&
            dyfProductState.divisionNumber?.toString() ===
              customerState.divisionNumber?.toString();

          return isReady;
        },
      ),
      map(() => true),
    );
  }

  loadProducts(): Observable<number[]> {
    // The inspired pick product numbers to load
    const inspiredProducts$ = this.readyForProductSelection$.pipe(
      filter(readyForSelection => !!readyForSelection),
      switchMap(() => this.orderService.getInspiredPickProductNumbers$()),
    );

    const productsForOrder$ = this.readyForProductSelection$.pipe(
      filter(readyForSelection => !!readyForSelection),
      switchMap(() => {
        return this.store.select(CART_SELECTORS.selectCart).pipe(
          filter(cartState => !cartState.loading),
          switchMap(cartState => {
            const productNumbers = cartState.orderItems.map(
              item => item.productNumber,
            );
            return of(productNumbers);
          }),
        );
      }),
    );

    const dyfProducts$ = this.readyForProductSelection$.pipe(
      filter(readyForSelection => !!readyForSelection),
      switchMap(() => {
        return this.orderService.getDyfProductNumbers$();
      }),
    );

    return combineLatest([
      dyfProducts$,
      inspiredProducts$,
      productsForOrder$,
    ]).pipe(
      map(([dyfProducts, inspiredProducts, productsForOrder]) => {
        const allProductNumbers = [
          ...dyfProducts,
          ...inspiredProducts,
          ...productsForOrder,
        ];
        const uniqueProductNumbers = Array.from(new Set(allProductNumbers));
        this.productService.loadProducts(uniqueProductNumbers);
        this.spinnerPromise
          .then(() => {
            this.spinner.dismissSpinnerModal();
          })
          .catch(error => {
            this.spinner.dismissSpinnerModal();
          });
        return uniqueProductNumbers;
      }),
      finalize(() => {
        this.spinnerPromise = Promise.resolve();
      }),
    );
  }

  fetchAndLoadFrequentlyBoughtTogether() {
    const isCartLoading$ = this.orderService.isCartLoading$();
    const orderHeader$ = this.orderService.getOrderHeader$();
    const selectedCustomerState$ =
      this.orderService.getSelectedCustomerState$();
    const orderItemState$ = this.orderService.getOrderItemState$();
    const isFeatureEnabled$ =
      this.orderService.getFrequentlyBoughtTogetherFlag$();
    combineLatest([
      isCartLoading$,
      orderHeader$,
      selectedCustomerState$,
      orderItemState$,
      isFeatureEnabled$,
    ])
      .pipe(
        filter(
          ([
            isCartLoading,
            orderHeader,
            selectedCustomerState,
            orderItemState,
          ]) =>
            !isCartLoading &&
            !!orderHeader &&
            !!selectedCustomerState &&
            (orderHeader.totalUnits === 0 ||
              Object.values(orderItemState?.entities)?.length > 0),
        ),
        take(1),
        switchMap(
          ([
            isCartLoading,
            orderHeader,
            selectedCustomerState,
            orderItemState,
            isFeatureEnabled,
          ]) => {
            const orderItems = Object.values(orderItemState?.entities);
            if (isFeatureEnabled && orderItems?.length > 0) {
              const cartLines = orderItems.map(orderItem => ({
                sku: `${selectedCustomerState?.divisionNumber}-${orderItem?.productNumber}`,
                pid: `${selectedCustomerState?.divisionNumber}-${orderItem?.productNumber}`,
                quantity: 1,
                currency: 'USD',
                value: '1.00',
              }));
              return this.merchandisingService.fetchMerchandising({
                experiences: [
                  {
                    name: FREQUENTLY_BOUGHT_TOGETHER_EXPERIENCE,
                    context: {
                      eventType: 'monetate:context:Cart',
                      cartLines,
                    },
                  },
                ],
              });
            } else {
              return of(false);
            }
          },
        ),
        filter(requestSent => requestSent),
        take(1),
        switchMap(requestSent => {
          return this.orderService.getFrequentlyBoughtTogetherProductNumbers$();
        }),
        filter(productNumbers => !!productNumbers),
        take(1),
      )
      .subscribe(productNumbers => {
        if (productNumbers?.length > 0) {
          this.productService.loadProducts(productNumbers);
        }
      });
  }

  loadMerchZoneProducts() {
    const merchZoneProducts$ = this.readyForProductSelection$.pipe(
      filter(readyForSelection => !!readyForSelection),
      switchMap(() =>
        this.store.select(
          selectMerchandisingResponseById(ReviewOrderMerchZone.experience),
        ),
      ),
      filter(
        (merchZone: any) =>
          !!merchZone && merchZone.loadingState === LoadingState.loaded,
      ),
      take(1),
      tap((merchZone: any) => {
        if (merchZone) {
          const productNumbers = merchZone.products.map(
            (product: any) => product.productNumber,
          );
          this.productService.loadProducts(productNumbers);
        }
      }),
    );

    if (this.merchZoneSubscription) {
      this.merchZoneSubscription.unsubscribe();
    }
    this.merchZoneSubscription = merchZoneProducts$.subscribe();
  }
}
